import { createBigDecimal, plus, times } from '@0xtorch/big-decimal'
import type { Transfer } from '@0xtorch/core'
import type { CostBasis } from '../../types/costBasis'
import type { IncomeSubCategory, Transaction } from '../../types/transaction'
import { createAssetKey } from '../../utils'
import type {
  CreateCategoryTransactionParameters,
  CreateCategoryTransactionReturnTypes,
} from './types'
import { addBalanceToAssetBorrowsAndAssetBalances } from './util/addBalanceToAssetBorrowsAndAssetBalances'

type CreateIncomeTransactionParameters = CreateCategoryTransactionParameters & {
  readonly subCategory?: IncomeSubCategory
  readonly transfer: Transfer
  readonly costBasis: CostBasis
}

type CreateIncomeTransactionReturnTypes = CreateCategoryTransactionReturnTypes

export const createIncomeTransaction = ({
  actionId,
  timestamp,
  subCategory,
  transfer,
  assetBalances,
  assetBorrows,
  usedBalances,
  nftAcquiredTimestamps,
  costBasis,
}: CreateIncomeTransactionParameters): CreateIncomeTransactionReturnTypes => {
  const key = createAssetKey(transfer.asset)
  const price = transfer.price ?? createBigDecimal(0n)
  const value = times(transfer.amount, price, 6)

  // Add balance
  const { assetBalances: keyAssetBalances, assetBorrows: keyAssetBorrows } =
    transfer.asset.type === 'FiatCurrency'
      ? {
          assetBalances: [],
          assetBorrows: [],
        }
      : addBalanceToAssetBorrowsAndAssetBalances({
          amount: transfer.amount,
          value,
          assetBalances: assetBalances.get(key) ?? [],
          assetBorrows: assetBorrows.get(key) ?? [],
          costBasis,
        })
  if (keyAssetBalances.length > 0) {
    assetBalances.set(key, keyAssetBalances)
  } else {
    assetBalances.delete(key)
  }
  if (keyAssetBorrows.length > 0) {
    assetBorrows.set(key, keyAssetBorrows)
  } else {
    assetBorrows.delete(key)
  }

  const balance = (assetBalances.get(key) ?? []).reduce(
    (acc, assetBalance) => plus(acc, assetBalance.amount),
    createBigDecimal(0n),
  )
  const borrowing = (assetBorrows.get(key) ?? []).reduce(
    (acc, assetBorrow) => plus(acc, assetBorrow.amount),
    createBigDecimal(0n),
  )

  const transaction: Transaction = {
    actionId,
    order: 0,
    timestamp,
    category: 'income',
    subCategory,
    ins: [
      {
        amount: transfer.amount,
        asset: transfer.asset,
        price,
        zeroCompleted: transfer.price === undefined,
        cost: value,
        balance,
        borrowing,
        feePl: createBigDecimal(0n),
        incomePl:
          subCategory === 'airdrop' || subCategory === 'realize-profit'
            ? createBigDecimal(0n)
            : value,
        tradePl: createBigDecimal(0n),
        separateTradePl: createBigDecimal(0n),
        nftTradePl: createBigDecimal(0n),
        nftCapitalPl: createBigDecimal(0n),
        separateRealizedPl: createBigDecimal(0n),
        realizedPl:
          subCategory === 'realize-profit' ? value : createBigDecimal(0n),
        airdropPl: subCategory === 'airdrop' ? value : createBigDecimal(0n),
        totalPl: value,
      },
    ],
    outs: [],
  }

  return {
    transaction,
    assetBalances,
    assetBorrows,
    usedBalances,
    nftAcquiredTimestamps,
  }
}
